import Dexie from 'dexie'

const db = new Dexie('documents') // Initialize Dexie instance with a database name

// Flag to prevent DB operations after close
let dbClosed = false

// Promise that resolves when DB initialization is complete
let dbInitializedResolver: () => void
let dbInitializedRejecter: (reason?: any) => void
const dbInitializedPromise: Promise<void> = new Promise((resolve, reject) => {
	dbInitializedResolver = resolve
	dbInitializedRejecter = reject
})

function syncDocumentsPlugin(store) {
	// Initialize the promise when the plugin is created

	async function clearModule(moduleName) {
		console.debug('Wiping module', moduleName)
		await db[moduleName].clear()
		// wipe the module state
		await db['moduleMetadata'].delete(moduleName)
	}

	async function putModuleMetadata(newMetadata) {
		const currentMetadata = await db['moduleMetadata'].get(newMetadata.moduleName)
		await db['moduleMetadata'].put({
			...currentMetadata,
			...newMetadata
		})
	}

	async function documentIsOutdated(moduleName, document) {
		if (!document || !document._id || !document.updatedAt) {
			console.warn(`Invalid document for ${moduleName}:`, document)
			return false // We can't determine if it's outdated, so we'll assume it's not
		}

		// Check if the table exists before querying
		if (!db[moduleName]) {
			return false
		}

		try {
			const existingDoc = await db[moduleName].get(document._id)

			if (existingDoc && existingDoc.updatedAt) {
				const incomingUpdateTime = new Date(document.updatedAt).getTime()
				const existingUpdateTime = new Date(existingDoc.updatedAt).getTime()

				if (incomingUpdateTime < existingUpdateTime) {
					console.warn(
						`Doc ${moduleName} ${document._id} not saving to Dexie. It's older than current version`,
						{
							incoming: document,
							existing: existingDoc
						}
					)
					return true
				}
			}

			return false // document is not outdated compared to existing record
		} catch (error) {
			console.error(`Error checking if document is outdated for ${moduleName}:`, error)
			return false // In case of error, we'll assume it's not outdated to be safe
		}
	}

	async function restoreModule(moduleName, expiredTime, populateResults) {
		const moduleMetadata = await db['moduleMetadata'].get(moduleName)
		if (
			moduleMetadata &&
			moduleMetadata.lastLoadDateLocal &&
			moduleMetadata.lastLoadDateLocal < expiredTime
		) {
			// wipe the table
			await clearModule(moduleName)
			populateResults[moduleName] = 'Expired. Wiped.'
		} else {
			const documents = (await db[moduleName].toArray()).reduce((acc, document) => {
				if (!document.deleted) {
					acc[document._id] = document
				}
				return acc
			}, {})
			populateResults[moduleName] = Object.keys(documents).length
			store.commit(`${moduleName}/RESTORE`, {
				documents,
				...moduleMetadata
			})
		}
	}

	async function initialize() {
		try {
			// Get the server startTime and git commit hash
			const serverStatus = await store.dispatch('app/getServerStatus')

			if (!serverStatus || !serverStatus.startTime) {
				console.error('Could not get server status or startTime. Using fallback database version.')
			}

			// Calculate the database version by finding the number of minutes since Jan 1, 2023, using the server start time.
			// each server restart will generate a new database version
			// as long as they are 1min apart at least
			const startTime = serverStatus?.startTime || Date.now()
			const dbVersion = Math.floor(
				(startTime - new Date('2023-09-01T00:00:00Z').getTime()) / (1000 * 60)
			)

			// Create the schemas for the stores
			const moduleNames = []
			const stores = {
				moduleMetadata: 'moduleName' // stores module metadata
			}
			for (const moduleName in store.state) {
				const hasDocuments = Object.prototype.hasOwnProperty.call(
					store.state[moduleName],
					'documents'
				)
				const hasLastLoadDate = Object.prototype.hasOwnProperty.call(
					store.state[moduleName],
					'lastLoadDate'
				)
				if (hasDocuments && hasLastLoadDate) {
					stores[moduleName] = '_id'
					moduleNames.push(moduleName)
				}
			}

			// Initialize Dexie
			db.version(dbVersion).stores(stores)
			await db.open()

			// Populate Vuex store with documents
			const populateResults = {}
			const expiredTime = Date.now() - store.state.app.documentCacheTTL

			// IMPORTANT: Restore user module LAST to prevent race conditions
			// When the user module is restored, isAuthenticated becomes true, which triggers
			// the authentication watcher that calls RESET_LAST_LOAD_DATE on all modules.
			// If other modules haven't been restored yet, their restoration will override
			// the reset values, causing lastLoadDate to return timestamps instead of 0.
			const nonUserModules = moduleNames.filter(name => name !== 'user')
			const userModule = moduleNames.includes('user') ? ['user'] : []

			// First, restore all non-user modules
			for (const moduleName of nonUserModules) {
				await restoreModule(moduleName, expiredTime, populateResults)
			}

			// Finally, restore the user module last to prevent authentication race conditions
			for (const moduleName of userModule) {
				await restoreModule(moduleName, expiredTime, populateResults)
			}

			store.commit('app/SET_STATE_RESTORED', true)
			console.log(`Dexie: v${dbVersion} w/ modules`, moduleNames)
			console.log(`Dexie: populated with`, populateResults)

			// Resolve the initialization promise
			dbInitializedResolver()

			// Subscribe to vuex mutations
			// to keep the database in sync with the store
			store.subscribe(async mutation => {
				if (dbClosed) return
				try {
					// Get the module name and type of mutation
					const [moduleName, mutationType] = mutation.type.split('/')

					// ensure this module is a document store
					if (
						Object.prototype.hasOwnProperty.call(store.state[moduleName], 'documents')
					) {
						// Get the payload
						const { payload } = mutation

						switch (mutationType) {
							case 'SET':
							case 'UPDATE': {
								if (!db[moduleName]) break
								if (await documentIsOutdated(moduleName, payload)) {
									console.log(
										'Sync: Aborting. Payload out of date.',
										moduleName,
										mutationType,
										payload
									)
									return // abort if the document is outdated
								}
								await db[moduleName].put(payload)
								break
							}
							case 'DELETE': {
								if (!db[moduleName]) break
								if (await documentIsOutdated(moduleName, payload)) {
									return // abort if the document is outdated
								}
								await db[moduleName].delete(payload)
								break
							}
							case 'CREATE_BULK': {
								if (db[moduleName]) {
									await db[moduleName].bulkPut(payload)
								}
								break
							}
							case 'SET_LAST_LOAD_DATE': {
								await putModuleMetadata({
									moduleName,
									lastLoadDate: payload,
									lastLoadDateLocal: Date.now()
								})
								break
							}
							case 'ON_DEAUTHENTICATE': {
								// wipe the table
								await clearModule(moduleName)
								break
							}
							case 'RESET_LAST_LOAD_DATE': {
								await clearModule(moduleName)
								await putModuleMetadata({
									moduleName,
									lastLoadDate: 0,
									lastLoadDateLocal: 0
								})
								break
							}
						}
					}
				} catch (e) {
					if (e.name === 'DatabaseClosedError') {
						console.warn('Sync: Database closed, ignoring error.')
						return
					}
					console.error(e)
				}
			})
		} catch (e) {
			console.error('Error initializing Dexie:', e)
			dbInitializedRejecter(e)
		}
	}

	// If the socket goes offline and re-connects
	// 1. delete and reinitialize the database
	// 2. Reload all documents from scratch
	store.watch(
		state => state.socket.reconnectSucceeded,
		async reconnectSucceeded => {
			// Ensure we are still authenticated.
			// Logging out can trigger a "reconnectSucceeded" event because it clears the "socket problem"
			// (which is defined as being authenticated but disconnected).
			// If we are logging out, we don't want to wipe the DB here because the logout process
			// will do it, and doing it here can cause race conditions (DatabaseClosedError).
			if (reconnectSucceeded && store.getters['user/isAuthenticated']) {
				console.log('Refreshing page as a result of socket reconnection')
				await store.dispatch('app/clearLocalCache', { refresh: true })
			}
		}
	)

	// Initialize the plugin
	initialize()
}

async function closeAndDeleteDb() {
	dbClosed = true
	await db.close()
	await db.delete()
}

export default syncDocumentsPlugin
export { db, dbInitializedPromise, closeAndDeleteDb }
